import React from 'react';
import { motion } from 'framer-motion';
import SectionShell from './Chapter';
import { SITE } from '@/constants/testIds';

/**
 * Selected work / representative transactions.
 * Client identities are held in confidence — records are described in
 * anonymised, factual terms consistent with the confidentiality boundary.
 */
const RECORDS = [
  {
    sector: 'Consumer & Retail',
    geo: 'India → GCC',
    mandate: 'Sell-side M&A',
    status: 'Closed',
    headline:
      'Cross-border sale of a founder-led branded consumer platform to a Middle-East strategic acquirer.',
  },
  {
    sector: 'Industrials & Manufacturing',
    geo: 'India',
    mandate: 'Growth Capital Raise',
    status: 'Closed',
    headline:
      'Structured minority round for a family-owned industrial platform with export exposure and expansion capex.',
  },
  {
    sector: 'Financial Services',
    geo: 'India · UAE',
    mandate: 'Strategic Advisory',
    status: 'Executed',
    headline:
      'Cross-border joint-venture framework between an Indian NBFC and a MENA family office for shared distribution.',
  },
  {
    sector: 'Technology & Digital',
    geo: 'India → Hong Kong',
    mandate: 'Sell-side M&A',
    status: 'Closed',
    headline:
      'Exit for founders of a B2B software platform to a strategic buyer with an Asia-Pacific consolidation thesis.',
  },
  {
    sector: 'Healthcare & Life Sciences',
    geo: 'India',
    mandate: 'Buy-side M&A',
    status: 'Signed',
    headline:
      'Acquisition of a specialty healthcare asset by a promoter-led platform pursuing a national roll-up strategy.',
  },
  {
    sector: 'Infrastructure & Real Assets',
    geo: 'MENA',
    mandate: 'Project Advisory',
    status: 'Executed',
    headline:
      'Feasibility, structuring and partner-selection for a large-scale infrastructure initiative in the Gulf region.',
  },
];

export const SelectedWork = () => {
  const ease = [0.22, 1, 0.36, 1];
  return (
    <SectionShell
      id="work"
      testId={SITE.work}
      theme="dark"
      index="06"
      eyebrow="Selected Work"
      title={
        <>
          Representative mandates — client identities held in confidence.
        </>
      }
      intro="A summary of the type of work we take on, described in the anonymised terms our engagement letters allow. Full case discussions are handled under NDA."
    >
      <div className="border-t border-[color:var(--rule-dark-2)]">
        {RECORDS.map((r, i) => (
          <motion.div
            key={r.headline}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-10% 0px' }}
            transition={{ duration: 0.7, delay: i * 0.06, ease }}
            className="grid grid-cols-12 gap-4 md:gap-10 py-8 md:py-10 border-b border-[color:var(--rule-dark)] hover:bg-[color:var(--ink-2)]/60 transition-colors"
          >
            <div className="col-span-12 md:col-span-2 flex md:block items-center gap-3">
              <span className="small-caps text-[10.5px] text-[color:var(--gold-2)]">
                {String(i + 1).padStart(2, '0')}
              </span>
              <span className="small-caps text-[10.5px] text-[color:var(--stone-3)] md:mt-2 md:block">
                {r.status}
              </span>
            </div>
            <div className="col-span-12 md:col-span-6">
              <p className="font-serif text-[19px] md:text-[22px] leading-[1.3] tracking-[-0.005em] text-[color:var(--stone)]">
                {r.headline}
              </p>
            </div>
            <div className="col-span-6 md:col-span-2 text-[13px] leading-[1.5] text-[color:var(--stone-2)]">
              <p className="small-caps text-[10.5px] text-[color:var(--stone-3)]">
                Sector
              </p>
              <p className="mt-1">{r.sector}</p>
            </div>
            <div className="col-span-6 md:col-span-2 text-[13px] leading-[1.5] text-[color:var(--stone-2)]">
              <p className="small-caps text-[10.5px] text-[color:var(--stone-3)]">
                Geography · Mandate
              </p>
              <p className="mt-1">
                {r.geo}
                <br />
                <span className="text-[color:var(--gold-2)]">{r.mandate}</span>
              </p>
            </div>
          </motion.div>
        ))}
      </div>

      <p className="mt-8 text-[12.5px] leading-[1.6] text-[color:var(--stone-3)] max-w-[620px]">
        Note: Transaction values, counterparties and identifying details are
        withheld in line with client confidentiality obligations. Complete case
        discussions are available under a mutual NDA.
      </p>
    </SectionShell>
  );
};

export default SelectedWork;
